import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePosts } from '@/hooks/usePosts';
import { useCategories } from '@/hooks/useCategories';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Post } from '@/hooks/usePosts';

const navLinks = [
  { label: 'INÍCIO', path: '/' },
  { label: 'SAÚDE', path: '/categoria/saude' },
  { label: 'ALIMENTAÇÃO', path: '/categoria/alimentacao' },
  { label: 'COMPORTAMENTO', path: '/categoria/comportamento' },
  { label: 'CURIOSIDADES', path: '/categoria/curiosidades' },
  { label: 'ADOÇÃO', path: '/categoria/adocao' },
];



function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-primary-100 text-primary-700 not-italic rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<Post[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const searchWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { getPublishedPosts } = usePosts();
  const { getCategoryLabel, getCategoryById } = useCategories();
  const { settings: siteSettings } = useSiteSettings();

  /* scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* close on route change */
  useEffect(() => {
    closeSearch();
    setMobileOpen(false);
  }, [location.pathname]);

  /* filter posts */
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) { setResults([]); setSelectedIndex(-1); return; }
    const all = getPublishedPosts();
    const filtered = all.filter(p => {
      const catLabels = (p.categories ?? []).map(id => getCategoryLabel(id).toLowerCase()).join(' ');
      return (
        p.title.toLowerCase().includes(q) ||
        p.subtitle.toLowerCase().includes(q) ||
        catLabels.includes(q)
      );
    }).slice(0, 6);
    setResults(filtered);
    setSelectedIndex(-1);
  }, [searchQuery]);

  /* click outside closes dropdown */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        closeSearch();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setResults([]);
    setSelectedIndex(-1);
  }, []);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const navigateTo = (post: Post) => {
    navigate(`/post/${post.slug}`);
    closeSearch();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      const target = selectedIndex >= 0 ? results[selectedIndex] : results[0];
      if (target) navigateTo(target);
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const showDropdown = searchOpen && searchQuery.trim().length > 0;

  return (
    <header
      className={`w-full z-50 sticky top-0 bg-background-50 border-b border-background-200 transition-shadow duration-300 ${
        scrolled ? 'shadow-sm' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full overflow-hidden border-2 border-primary-500">
            {siteSettings?.logoUrl ? (
              <img
                src={siteSettings.logoUrl}
                alt={siteSettings?.siteName || 'Miaudecasa'}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src="https://readdy.ai/api/search-image?query=Cute%20cartoon%20style%20orange%20tabby%20cat%20face%20portrait%20illustration%20on%20clean%20white%20background%2C%20friendly%20expression%2C%20round%20eyes%2C%20minimalist%20design%2C%20warm%20orange%20tones%2C%20adorable%20pet%20mascot%20design&width=120&height=120&seq=logo-miaudecasa-cat-01&orientation=squarish"
                alt="Miaudecasa logo"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-heading font-bold text-primary-500 text-base md:text-lg leading-none">{siteSettings?.siteName || 'miaudecasa'}</div>
            {siteSettings?.siteSubtitle && (
              <div className="text-foreground-500 text-xs">{siteSettings.siteSubtitle}</div>
            )}
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`
                relative text-xs font-bold tracking-widest whitespace-nowrap cursor-pointer
                transition-colors duration-200 pb-0.5
                after:absolute after:bottom-0 after:left-0
                after:h-[2px] after:rounded-full after:bg-primary-500
                after:transition-all after:duration-300
                ${isActive(link.path)
                  ? 'text-primary-500 after:w-full'
                  : 'text-foreground-700 hover:text-primary-500 after:w-0 hover:after:w-full'}
              `}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Search area + mobile toggle */}
        <div className="flex items-center gap-2">

          {/* Search wrapper — keeps dropdown anchored */}
          <div ref={searchWrapRef} className="relative">
            <div className={`flex items-center transition-all duration-300 ${
              searchOpen ? 'gap-2' : 'gap-0'
            }`}>
              {/* Input — slides in */}
              <div
                className="overflow-hidden transition-all duration-300 ease-out"
                style={{ width: searchOpen ? 'clamp(140px, 50vw, 220px)' : '0px', opacity: searchOpen ? 1 : 0 }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Buscar artigos..."
                  className="w-full text-sm border border-background-300 rounded-full px-4 py-2 outline-none focus:border-primary-400 bg-background-50 whitespace-nowrap"
                />
              </div>

              {/* Search / Close toggle */}
              <button
                onClick={searchOpen ? closeSearch : openSearch}
                className="cursor-pointer text-foreground-700 hover:text-primary-500 transition-colors w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 flex-shrink-0"
                aria-label={searchOpen ? 'Fechar busca' : 'Abrir busca'}
              >
                <i className={`${searchOpen ? 'ri-close-line' : 'ri-search-line'} text-xl`}></i>
              </button>
            </div>

            {/* ── Search Dropdown ── */}
            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-[90vw] sm:w-[380px] max-w-[380px] bg-background-50 border border-background-200 rounded-xl overflow-hidden z-50"
                style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.10)' }}
              >
                {results.length > 0 ? (
                  <>
                    <div className="px-4 py-2.5 border-b border-background-100">
                      <p className="text-xs text-foreground-400">
                        <span className="font-bold text-foreground-700">{results.length}</span>
                        {results.length === 1 ? ' resultado' : ' resultados'} para{' '}
                        <span className="text-primary-500 font-bold">&ldquo;{searchQuery}&rdquo;</span>
                      </p>
                    </div>

                    <ul>
                      {results.map((post, i) => (
                        <li key={post.id}>
                          <button
                            onMouseDown={() => navigateTo(post)}
                            onMouseEnter={() => setSelectedIndex(i)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer transition-colors ${
                              i === selectedIndex ? 'bg-primary-50' : 'hover:bg-background-100'
                            } ${i < results.length - 1 ? 'border-b border-background-100' : ''}`}
                          >
                            {/* Thumbnail */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <span className="text-primary-500 text-xs font-bold uppercase tracking-wide block mb-0.5">
                                {getCategoryById(post.categories?.[0] ?? '')?.label ?? ''}
                              </span>
                              <p className="text-foreground-950 text-sm font-bold leading-tight line-clamp-1">
                                <HighlightText text={post.title} query={searchQuery} />
                              </p>
                              <p className="text-foreground-400 text-xs mt-0.5">{post.readTime}</p>
                            </div>

                            <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0"></i>
                          </button>
                        </li>
                      ))}
                    </ul>

                    <div className="px-4 py-2.5 border-t border-background-100 bg-background-100/50">
                      <p className="text-xs text-foreground-400 text-center">
                        Use <kbd className="bg-background-200 text-foreground-600 px-1.5 py-0.5 rounded text-xs font-mono">↑↓</kbd> para navegar,{' '}
                        <kbd className="bg-background-200 text-foreground-600 px-1.5 py-0.5 rounded text-xs font-mono">Enter</kbd> para abrir,{' '}
                        <kbd className="bg-background-200 text-foreground-600 px-1.5 py-0.5 rounded text-xs font-mono">Esc</kbd> para fechar
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-3">
                      <i className="ri-search-line text-xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-700 text-sm font-bold mb-1">Nenhum resultado</p>
                    <p className="text-foreground-400 text-xs">
                      Nada encontrado para{' '}
                      <span className="text-primary-500 font-bold">&ldquo;{searchQuery}&rdquo;</span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden cursor-pointer text-foreground-700 hover:text-primary-500 w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50 border-t border-background-100 px-4 py-4 flex flex-col gap-1">
          {/* Mobile search */}
          <div className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar artigos..."
              className="w-full text-sm border border-background-300 rounded-full pl-9 pr-4 py-2 outline-none focus:border-primary-400 bg-background-50"
            />
          </div>

          {/* Mobile results */}
          {searchQuery.trim() && (
            <div className="mb-3 border border-background-200 rounded-xl overflow-hidden">
              {results.length > 0 ? results.map((post, i) => (
                <button
                  key={post.id}
                  onMouseDown={() => navigateTo(post)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer hover:bg-background-100 transition-colors ${
                    i < results.length - 1 ? 'border-b border-background-100' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-primary-500 text-xs font-bold uppercase tracking-wide block">
                      {getCategoryById(post.categories?.[0] ?? '')?.label ?? ''}
                    </span>
                    <p className="text-foreground-950 text-xs font-bold line-clamp-1">
                      <HighlightText text={post.title} query={searchQuery} />
                    </p>
                  </div>
                </button>
              )) : (
                <p className="text-center text-foreground-400 text-xs py-4">Nenhum resultado encontrado</p>
              )}
            </div>
          )}

          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-bold tracking-widest py-2.5 px-2 rounded-lg cursor-pointer transition-colors ${
                isActive(link.path)
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}
